import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box, Snackbar, Alert, Slide, useMediaQuery } from "@mui/material";
import MenuBar from "../components/navigation/MenuBar/MenuBar";
import Footer from "../components/layout/Footer";
import NewsLetter from "../components/marketing/NewsLetter";
import { trackEvent } from "../utils/analytics";

import mexicanEloteStyleBeansImg from "../assets/products/beans/MEXICAN_ELOTE_STYLE_PINTO_AND_BLACK_BEANS/MEXICAN_ELOTE_STYLE_PINTO_AND_BLACK_BEANS.png";
import streetCornTamaleImg from "../assets/products/tamales/STREET_CORN_TAMALE/STREET_CORN_TAMALE.png";
import mexicanStreetCornPouchImg from "../assets/products/rices/pouches/MEXICAN_STREET_CORN_RICE/MEXICAN_STREET_CORN_RICE.png";
import mexicanStreetCornCupImg from "../assets/products/rices/cups/MEXICAN_STREET_CORN_RICE_CUP/MEXICAN_STREET_CORN_RICE_CUP.png";
import smNewsletterBackground from "../assets/backgrounds/SM_newsletterBackground.jpg";

const NEWSLETTER_NEXT_ALLOWED_AT_KEY = "sm_newsletter_nextAllowedAt";
const VISIT_COUNT_KEY = "sm_newsletter_visit_count";
const FIRST_VISIT_AT_KEY = "sm_newsletter_first_visit_at";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const REQUIRED_VISITS = 5;

export default function AppLayout() {
    const location = useLocation();
    const [showNewsletter, setShowNewsletter] = useState(false);

    // Snackbar lives here so it doesn't unmount with the modal
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // bottom-right on desktop, bottom-center on mobile/tablet
    const isSmallToMd = useMediaQuery((theme) => theme.breakpoints.down("lg"));
    const snackbarAnchor = useMemo(
        () => ({ vertical: "bottom", horizontal: isSmallToMd ? "center" : "right" }),
        [isSmallToMd]
    );

    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbarOpen(false);
    };

    useEffect(() => {
        const now = Date.now();

        // Cooldown check
        const nextAllowedAt =
            Number(localStorage.getItem(NEWSLETTER_NEXT_ALLOWED_AT_KEY)) || 0;
        if (now < nextAllowedAt) return;

        const firstVisitAtRaw = localStorage.getItem(FIRST_VISIT_AT_KEY);
        const visitCountRaw = localStorage.getItem(VISIT_COUNT_KEY);

        const firstVisitAt = firstVisitAtRaw ? Number(firstVisitAtRaw) : 0;
        const prevVisitCount = visitCountRaw ? Number(visitCountRaw) : 0;

        const isNewWindow = !firstVisitAt || now - firstVisitAt > SEVEN_DAYS_MS;

        // Start or reset the 7-day window
        if (isNewWindow) {
            localStorage.setItem(FIRST_VISIT_AT_KEY, String(now));
            localStorage.setItem(VISIT_COUNT_KEY, "1");

            // ✅ Show on first visit of the window (including true first visit)
            setShowNewsletter(true);
            trackEvent("newsletter_shown", { trigger: "first_visit" });
            return;
        }

        // Increment within the window
        const nextVisitCount = prevVisitCount + 1;
        localStorage.setItem(VISIT_COUNT_KEY, String(nextVisitCount));

        // ✅ Show when threshold is reached
        if (nextVisitCount >= REQUIRED_VISITS) {
            setShowNewsletter(true);
            trackEvent("newsletter_shown", { trigger: "visit_threshold" });
        }
    }, [location.pathname]);

    const snoozeFor7Days = () => {
        const now = Date.now();
        localStorage.setItem(
            NEWSLETTER_NEXT_ALLOWED_AT_KEY,
            String(now + SEVEN_DAYS_MS)
        );

        // Optional: reset visit tracking
        localStorage.removeItem(VISIT_COUNT_KEY);
        localStorage.removeItem(FIRST_VISIT_AT_KEY);

        setShowNewsletter(false);
    };

    const handleSubmit = async () => {
        trackEvent("generate_lead", { method: "newsletter" });

        // Show snackbar (layout-level, so it always displays)
        setSnackbarOpen(true);

        // Close modal + snooze
        snoozeFor7Days();
    };

    const handleSkip = () => {
        trackEvent("newsletter_dismiss", { method: "skip" });
        snoozeFor7Days();
    };

    const handleClose = () => {
        trackEvent("newsletter_dismiss", { method: "close" });
        snoozeFor7Days();
    };

    return (
        <Box sx={{ minHeight: "100vh" }}>
            <MenuBar
                items={[
                    {
                        id: "products",
                        label: "Products",
                        to: "/products",
                        hasDropdown: true,
                        dropdownItems: [
                            {
                                id: "bestSellers",
                                label: "Best Sellers",
                                to: "/products?tab=bestsellers",
                                bg: "#792B25",
                                imageSrc: mexicanStreetCornCupImg,
                                leftImage: { width: 140, height: 100, transform: "translate(0%, 0%)" },
                            },
                            {
                                id: "beans",
                                label: "Beans",
                                to: "/products?tab=beans",
                                bg: "#81634B",
                                imageSrc: mexicanEloteStyleBeansImg,
                                leftImage: { width: 90, height: 90, transform: "translate(0%, 15%)" },
                            },
                            {
                                id: "tamales",
                                label: "Tamales",
                                to: "/products?tab=tamales",
                                bg: "#F8A716",
                                imageSrc: streetCornTamaleImg,
                                leftImage: { width: 90, height: 90, transform: "translate(0%, 20%)" },
                            },
                            {
                                id: "rices",
                                label: "Rices",
                                to: "/products?tab=rices",
                                bg: "#525E3B",
                                imageSrc: mexicanStreetCornPouchImg,
                                leftImage: { width: 90, height: 90, transform: "translate(0%, 15%)" },
                            },
                        ],
                    },
                    { id: "essence", label: "Essence", to: "/essence" },
                    { id: "moments", label: "Moments", to: "/moments" },
                    { id: "wheretobuy", label: "Where to Buy", to: "/wheretobuy" },
                ]}
                logoSrc="/images/SM_brownLogo.png"
                logoAlt="San Miguel Family Foods"
            />

            <Outlet />
            <Footer />

            {showNewsletter && (
                <Box sx={{ position: "fixed", inset: 0, zIndex: 2000 }}>
                    <NewsLetter
                        heroImage={smNewsletterBackground}
                        logoSrc="/images/SM_brownLogo.png"
                        onSubmit={handleSubmit}
                        onSkip={handleSkip}
                        onClose={handleClose}
                    />
                </Box>
            )}

            {/* ✅ Snackbar using slots API (no deprecated TransitionComponent) */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={snackbarAnchor}
                slots={{ transition: Slide }}
                slotProps={{ transition: { direction: "up" } }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    icon={false}
                    severity="success"
                    sx={(theme) => ({
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        backgroundColor: "rgba(255, 255, 255, 0.85)",
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: 3,
                        color: theme.palette.text.primary,
                        fontFamily: "'League Spartan', Arial, sans-serif",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",

                        "& .MuiAlert-message": {
                            padding: 0,
                            lineHeight: 1.4,
                        },
                    })}
                >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                        Welcome to the San Miguel Family
                    </Box>
                    <br />
                    <Box component="span">
                        Your email has been successfully added to our newsletter.
                    </Box>
                </Alert>
            </Snackbar>
        </Box>
    );
}